import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Tag, Track } from '@app/core/models/Track';
import { AppState } from '@app/core/states/app.state';
import { Stop } from '@app/core/states/audio.actions';
import { LogModal } from '@app/core/states/events-tracking.actions';
import { AddTagModalComponent } from '@app/library/components/modals/add-tag-modal/add-tag-modal.component';
import { ArchiveTrackModalComponent } from '@app/library/components/modals/archive-track-modal/archive-track-modal.component';
import { ClearBacModalComponent } from '@app/library/components/modals/clear-bac-modal/clear-bac-modal.component';
import { DeleteBacModalComponent } from '@app/library/components/modals/delete-bac-modal/delete-bac-modal.component';
import { DeletePlaylistModalComponent } from '@app/library/components/modals/delete-playlist-modal/delete-playlist-modal.component';
import { DeleteTagModalComponent } from '@app/library/components/modals/delete-tag-modal/delete-tag-modal.component';
import { DuplicatePlaylistModalComponent } from '@app/library/components/modals/duplicate-playlist-modal/duplicate-playlist-modal.component';
import { EditMultipleTrackModalComponent } from '@app/library/components/modals/edit-multiple-track-modal/edit-multiple-track-modal.component';
import { MixPointsModalComponent } from '@app/library/components/modals/mix-points-modal/mix-points-modal.component';
import { MoveTrackModalComponent } from '@app/library/components/modals/move-track-modal/move-track-modal.component';
import { PlaylistUploadBoxModalComponent } from '@app/library/components/modals/playlist-upload-box-modal/playlist-upload-box-modal.component';
import { RemoveTagsModalComponent } from '@app/library/components/modals/remove-tags-modal/remove-tags-modal.component';
import { Bac, RootBac } from '@app/library/models/bac.model';
import { Playlist } from '@app/library/models/playlist.model';
import { ArchivedTrack } from '@app/library/models/trash.model';
import { TrackApiModel } from '@app/library/services/track.service';
import { AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { MIXPOINT_MODAL_ID, MOBILE_DIALOG_CONFIG } from '@app/shared/constants';
import { Store } from '@ngxs/store';

import { DeleteTrackModalComponent } from '../components/modals/delete-track-modal/delete-track-modal.component';

import { AddDuplicateModalComponent } from './../components/modals/add-duplicate-modal/add-duplicate-modal.component';
import { AddToPlaylistModalComponent } from './../components/modals/add-to-playlist-modal/add-to-playlist-modal.component';
import { TracksState } from './../states/tracks.state';

@Injectable()
export class LibraryModalService {
  constructor(
    private readonly store: Store,
    private readonly dialog: MatDialog,
  ) {}

  clearBac(bac: Bac) {
    this.dialog.open(ClearBacModalComponent, {
      data: bac,
    });
  }

  deleteBac(bac: Bac) {
    this.dialog.open(DeleteBacModalComponent, {
      data: bac,
    });
  }

  archiveTrack(track: Track, bac: Bac, shortcut = false): void {
    this.dialog
      .open(ArchiveTrackModalComponent, {
        data: {
          bac,
          track,
          shortcut,
          isSingle: true,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.ARCHIVE_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  archiveMultipleTracks(trackIdList: number[], bac: Bac, shortcut = false): void {
    if (!trackIdList.length) {
      return;
    }
    this.dialog
      .open(ArchiveTrackModalComponent, {
        data: {
          bac,
          trackIdList,
          shortcut,
          isSingle: false,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.ARCHIVE_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  deleteArchivedTrack(archivedTrack: ArchivedTrack): void {
    this.dialog
      .open(DeleteTrackModalComponent, {
        data: {
          archivedTrack,
          isSingle: true,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.DELETE_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  deleteMultipleArchivedTracks(archivedTrackIdList: number[], isAll = false): void {
    this.dialog
      .open(DeleteTrackModalComponent, {
        data: {
          archivedTrackIdList,
          isSingle: false,
          isAll,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.DELETE_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  moveTrack(track: Track, targetBac: Bac): void {
    this.dialog
      .open(MoveTrackModalComponent, {
        data: {
          track,
          bac: targetBac,
          isSingle: true,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.MOVE_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  editMultipleTrack(
    trackIdList: number[] | Track[],
    track: Partial<TrackApiModel>,
    tracks?: Track[],
    file?: File,
  ): void {
    this.dialog
      .open(EditMultipleTrackModalComponent, {
        data: {
          trackIdList,
          track,
          tracks,
          file,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.CONFIRM_EDIT_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  moveMultipleTrack(trackIdList: number[], targetBac: Bac, currentBac?: RootBac): void {
    this.dialog
      .open(MoveTrackModalComponent, {
        data: {
          trackIdList,
          currentBac,
          bac: targetBac,
          isSingle: false,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.MOVE_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  addTagsToTrack(selectedTracks: number[]) {
    if (!selectedTracks.length) {
      return;
    }
    this.dialog
      .open(AddTagModalComponent, {
        data: {
          tracksId: selectedTracks,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.TAG_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  editMixPoints(track: Track) {
    this.store.dispatch(new Stop());
    this.dialog
      .open(MixPointsModalComponent, {
        data: {
          track,
        },
        id: MIXPOINT_MODAL_ID,
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.EDIT_MIXPOINT_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  addToPlaylist(selectedTracks: number[]) {
    this.dialog
      .open(AddToPlaylistModalComponent, {
        data: {
          trackIdList: selectedTracks,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.ADD_TO_PLAYLIST,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  addDuplicateToPlaylist(
    tracks: number[],
    playlistId: number,
    position: number,
    isMobile: boolean,
  ) {
    if (!isMobile) {
      this.dialog.closeAll();
    }
    if (tracks.length > 1) {
      this.dialog
        .open(AddDuplicateModalComponent, {
          data: {
            playlistId,
            position,
            trackIdList: tracks,
            isSingle: false,
          },
        })
        .afterOpened()
        .subscribe(() => this.logAddDuplicate());

      return;
    }
    const track = this.store.selectSnapshot(TracksState.trackWithId)(tracks[0]);
    const onMobile = this.store.selectSnapshot(AppState.onMobile);
    const config = onMobile
      ? {
          ...MOBILE_DIALOG_CONFIG,
          data: {
            playlistId,
            position,
            track,
            isSingle: true,
          },
        }
      : {
          data: {
            playlistId,
            position,
            track,
            isSingle: true,
          },
        };
    this.dialog
      .open(AddDuplicateModalComponent, config)
      .afterOpened()
      .subscribe(() => this.logAddDuplicate());
  }

  logAddDuplicate() {
    this.store.dispatch(
      new LogModal({
        name: AMPLITUDE_EVENT.MODAL.ADD_DUPLICATE_PLAYLIST,
        origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
      }),
    );
  }

  deletePlaylist(playlist: Playlist) {
    this.dialog.closeAll();
    this.dialog
      .open(DeletePlaylistModalComponent, {
        data: {
          playlist,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.DELETE_PLAYLIST,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  duplicatePlaylist(playlist: Playlist) {
    this.dialog.closeAll();
    this.dialog
      .open(DuplicatePlaylistModalComponent, {
        data: {
          playlist,
        },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.DUPLICATE_PLAYLIST,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  deleteTag(tag: Tag, tracks: number) {
    this.dialog.closeAll();
    this.dialog
      .open(DeleteTagModalComponent, {
        data: { tag, tracks },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.DELETE_TAG,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  removeTagsFromTracks(tracks: Track[]) {
    if (!tracks.length) {
      return;
    }
    this.dialog.closeAll();
    this.dialog
      .open(RemoveTagsModalComponent, {
        data: { tracks },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.REMOVE_TAG_FROM_TRACK,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }

  playlistUploadBox(files: FileList, playlist: Playlist) {
    this.dialog.closeAll();
    this.dialog
      .open(PlaylistUploadBoxModalComponent, {
        data: { files, playlist },
      })
      .afterOpened()
      .subscribe(() => {
        this.store.dispatch(
          new LogModal({
            name: AMPLITUDE_EVENT.MODAL.UPLOAD_AND_ADD_PLAYLIST,
            origin: AMPLITUDE_EVENT.ORIGIN.LIBRARY,
          }),
        );
      });
  }
}
